import { useEffect, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Plug,
  Printer as PrinterIcon,
  Save,
  Copy,
  Info,
  TestTube2,
  Lock,
  Palette,
} from "lucide-react";
import { toast } from "sonner";
import { useOutlet } from "@/contexts/OutletContext";
import { THEMES, applyTheme, getCurrentTheme } from "@/lib/theme";

const DEFAULTS = {
  integrations: {
    zomato: { enabled: false, api_key: "", restaurant_id: "", outlet_id: "" },
    swiggy: { enabled: false, api_key: "", restaurant_id: "", outlet_id: "" },
  },
  printer: {
    enabled: false,
    type: "browser",
    ip: "",
    port: 9100,
    paper_width: 80,
    auto_print_kot: false,
    auto_print_bill: false,
  },
};

const randKey = () => {
  const arr = new Uint8Array(24);
  crypto.getRandomValues(arr);
  return [...arr].map((b) => b.toString(16).padStart(2, "0")).join("");
};

export default function Settings() {
  const { outlets } = useOutlet();
  const [s, setS] = useState(DEFAULTS);
  const [tab, setTab] = useState("integrations");
  const [dev, setDev] = useState({
    password: "",
    max_outlets: 0,
    new_password: "",
  });
  const [devUnlocked, setDevUnlocked] = useState(false);
  const [theme, setTheme] = useState(getCurrentTheme());

  useEffect(() => {
    api
      .get("/settings", { params: { outlet_id: "" } })
      .then((r) => {
        setS({
          integrations: {
            ...DEFAULTS.integrations,
            ...(r.data.integrations || {}),
          },
          printer: { ...DEFAULTS.printer, ...(r.data.printer || {}) },
        });
        setDev((d) => ({
          ...d,
          max_outlets: r.data?.developer?.max_outlets || 0,
        }));
        if (r.data?.theme) {
          setTheme(r.data.theme);
          applyTheme(r.data.theme);
        }
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const pickTheme = async (k) => {
    setTheme(k);
    applyTheme(k);
    try {
      await api.put("/settings", { theme: k });
      toast.success(`Theme: ${THEMES[k].label}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setIntegration = (k, patch) =>
    setS((prev) => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        [k]: { ...prev.integrations[k], ...patch },
      },
    }));

  const setPrinter = (patch) =>
    setS((prev) => ({ ...prev, printer: { ...prev.printer, ...patch } }));

  const save = async () => {
    try {
      await api.put("/settings", s);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyUrl = (provider) => {
    const url = `${API_BASE}/webhooks/${provider}`;
    navigator.clipboard.writeText(url);
    toast.success("Webhook URL copied");
  };

  const testProvider = async (provider) => {
    try {
      const { data } = await api.post(`/integrations/${provider}/test`, {});
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveDev = async () => {
    try {
      const { data } = await api.put("/dev-settings", {
        password: dev.password,
        max_outlets: Number(dev.max_outlets || 0),
        new_password: dev.new_password || null,
      });
      toast.success(`Outlet limit set to ${data.max_outlets || "Unlimited"}`);
      setDevUnlocked(true);
      setDev((d) => ({ ...d, new_password: "" }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const IntegrationCard = ({ provider, label, color }) => {
    const v = s.integrations[provider];
    return (
      <Card
        className="p-5 border-[#EBE8E2] bg-white"
        data-testid={`integration-${provider}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center text-white font-heading font-extrabold"
              style={{ backgroundColor: color }}
            >
              {label[0]}
            </div>
            <div>
              <div className="font-heading font-bold text-lg text-[#2D2C2A]">
                {label}
              </div>
              <div className="text-xs text-[#7A7873]">
                Receive live online orders
              </div>
            </div>
          </div>
          <Switch
            checked={!!v.enabled}
            onCheckedChange={(c) => setIntegration(provider, { enabled: c })}
            data-testid={`toggle-${provider}`}
          />
        </div>

        <div
          className={`space-y-3 transition-opacity ${v.enabled ? "" : "opacity-60 pointer-events-none"}`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">
                Restaurant ID (from partner dashboard)
              </Label>
              <Input
                value={v.restaurant_id}
                onChange={(e) =>
                  setIntegration(provider, { restaurant_id: e.target.value })
                }
                placeholder={`${label} restaurant id`}
                data-testid={`${provider}-restid`}
              />
            </div>
            <div>
              <Label className="text-xs">
                Default Outlet for Incoming Orders
              </Label>
              <Select
                value={v.outlet_id || ""}
                onValueChange={(val) =>
                  setIntegration(provider, { outlet_id: val })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick outlet" />
                </SelectTrigger>
                <SelectContent>
                  {outlets.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">
              API Key (shared secret for webhook auth)
            </Label>
            <div className="flex gap-2">
              <Input
                value={v.api_key}
                onChange={(e) =>
                  setIntegration(provider, { api_key: e.target.value })
                }
                placeholder="api key"
                className="font-mono-num text-xs"
                data-testid={`${provider}-apikey`}
              />
              <Button
                variant="outline"
                type="button"
                onClick={() => setIntegration(provider, { api_key: randKey() })}
              >
                Generate
              </Button>
            </div>
          </div>
          <div className="bg-[#FBFAF8] border border-[#EBE8E2] rounded-md p-3 text-xs space-y-2">
            <div className="font-semibold text-[#2D2C2A]">How to connect</div>
            <div className="text-[#7A7873]">
              1. Generate an API key above and save settings.
              <br />
              2. Share this webhook URL + API key with your{" "}
              <strong>{label} partner-onboarding</strong> contact.
              <br />
              3. {label} will POST each new order to:
            </div>
            <div className="flex items-center gap-2 bg-white border border-[#EBE8E2] rounded px-2 py-1">
              <code className="font-mono-num text-[11px] flex-1 break-all">
                {API_BASE}/webhooks/{provider}
              </code>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={() => copyUrl(provider)}
                data-testid={`copy-${provider}`}
              >
                <Copy size={12} />
              </Button>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => testProvider(provider)}
              data-testid={`test-${provider}`}
            >
              <TestTube2 size={12} className="mr-1.5" /> Validate config
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="p-6 sm:p-8" data-testid="settings-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A]">
            Settings
          </h1>
          <p className="text-sm text-[#7A7873] mt-1">
            Online ordering integrations & printer hardware.
          </p>
        </div>
        <Button
          onClick={save}
          className="bg-[#D95D39] hover:bg-[#C24F2C]"
          data-testid="save-settings"
        >
          <Save size={14} className="mr-1.5" /> Save Settings
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#EBE8E2]">
          <TabsTrigger value="integrations" data-testid="tab-integrations">
            <Plug size={14} className="mr-1.5" /> Integrations
          </TabsTrigger>
          <TabsTrigger value="printer" data-testid="tab-printer">
            <PrinterIcon size={14} className="mr-1.5" /> Printer
          </TabsTrigger>
          <TabsTrigger value="developer" data-testid="tab-developer">
            <Lock size={14} className="mr-1.5" /> Developer
          </TabsTrigger>
          <TabsTrigger value="theme" data-testid="tab-theme">
            <Palette size={14} className="mr-1.5" /> Theme
          </TabsTrigger>
        </TabsList>

        <TabsContent value="integrations" className="mt-4 space-y-4">
          <div className="bg-[#FFF6E8] border border-[#F4D58B] text-[#7A5A18] rounded-lg p-3 text-sm flex gap-2">
            <Info size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              Zomato / Swiggy <strong>POS APIs are partner-only</strong>. Toggle
              these on, generate the API key, and forward the webhook URL to
              your aggregator account-manager. Until your partnership is
              approved you can also POST test orders to the webhook URL
              manually.
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <IntegrationCard provider="zomato" label="Zomato" color="#E23744" />
            <IntegrationCard provider="swiggy" label="Swiggy" color="#FC8019" />
          </div>
        </TabsContent>

        <TabsContent value="printer" className="mt-4">
          <Card className="p-5 border-[#EBE8E2] bg-white max-w-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading font-bold text-lg text-[#2D2C2A]">
                  Thermal Printer
                </div>
                <div className="text-xs text-[#7A7873]">
                  Configure KOT &amp; bill auto-printing.
                </div>
              </div>
              <Switch
                checked={!!s.printer.enabled}
                onCheckedChange={(c) => setPrinter({ enabled: c })}
                data-testid="printer-toggle"
              />
            </div>

            <div
              className={`space-y-3 transition-opacity ${s.printer.enabled ? "" : "opacity-60 pointer-events-none"}`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Printer Type</Label>
                  <Select
                    value={s.printer.type}
                    onValueChange={(v) => setPrinter({ type: v })}
                  >
                    <SelectTrigger data-testid="printer-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="browser">
                        Browser (window.print dialog)
                      </SelectItem>
                      <SelectItem value="network">
                        Network (ESC/POS over TCP)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Paper Width</Label>
                  <Select
                    value={String(s.printer.paper_width)}
                    onValueChange={(v) =>
                      setPrinter({ paper_width: Number(v) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="58">58 mm (32 cols)</SelectItem>
                      <SelectItem value="80">80 mm (48 cols)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {s.printer.type === "network" && (
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
                  <div>
                    <Label className="text-xs">Printer IP Address</Label>
                    <Input
                      value={s.printer.ip}
                      onChange={(e) => setPrinter({ ip: e.target.value })}
                      placeholder="192.168.1.50"
                      className="font-mono-num"
                      data-testid="printer-ip"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Port</Label>
                    <Input
                      type="number"
                      value={s.printer.port}
                      onChange={(e) =>
                        setPrinter({ port: Number(e.target.value) })
                      }
                      className="font-mono-num"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between border border-[#EBE8E2] rounded-md px-3 py-2.5">
                <div>
                  <div className="text-sm font-medium">
                    Auto-print KOT on save
                  </div>
                  <div className="text-xs text-[#7A7873]">
                    Open print dialog (or send to printer) when KOT button is
                    pressed
                  </div>
                </div>
                <Switch
                  checked={!!s.printer.auto_print_kot}
                  onCheckedChange={(c) => setPrinter({ auto_print_kot: c })}
                  data-testid="auto-print-kot"
                />
              </div>

              <div className="flex items-center justify-between border border-[#EBE8E2] rounded-md px-3 py-2.5">
                <div>
                  <div className="text-sm font-medium">
                    Auto-print bill on payment
                  </div>
                  <div className="text-xs text-[#7A7873]">
                    Trigger print right after capturing payment
                  </div>
                </div>
                <Switch
                  checked={!!s.printer.auto_print_bill}
                  onCheckedChange={(c) => setPrinter({ auto_print_bill: c })}
                  data-testid="auto-print-bill"
                />
              </div>

              {s.printer.type === "network" && (
                <div className="bg-[#FFF6E8] border border-[#F4D58B] text-[#7A5A18] rounded-md p-3 text-xs">
                  <strong>Network printing</strong> sends raw ESC/POS bytes to
                  the printer's TCP port (default 9100). Works only when the{" "}
                  <strong>
                    backend server can reach the printer on the LAN
                  </strong>
                  . On shared/cloud hosting, install a small{" "}
                  <strong>local print agent</strong> on a machine inside the
                  restaurant network.
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="developer" className="mt-4">
          <Card className="p-5 border-[#EBE8E2] bg-white max-w-xl space-y-4">
            <div className="flex items-center gap-2">
              <Lock size={18} className="text-[#D95D39]" />
              <div>
                <div className="font-heading font-bold text-lg text-[#2D2C2A]">
                  Developer Limits
                </div>
                <div className="text-xs text-[#7A7873]">
                  Password-protected. Sold per number of outlets.
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Developer password *</Label>
              <Input
                type="password"
                value={dev.password}
                onChange={(e) => setDev({ ...dev, password: e.target.value })}
                placeholder="default: dev-change-me"
                data-testid="dev-password"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Max Outlets Allowed</Label>
                <Input
                  type="number"
                  min="0"
                  value={dev.max_outlets}
                  onChange={(e) =>
                    setDev({ ...dev, max_outlets: e.target.value })
                  }
                  data-testid="dev-max-outlets"
                />
                <div className="text-[11px] text-[#7A7873] mt-1">
                  0 = unlimited. Currently used:{" "}
                  <strong>{outlets.length}</strong>
                </div>
              </div>
              <div>
                <Label className="text-xs">
                  Rotate Dev Password (optional)
                </Label>
                <Input
                  type="password"
                  value={dev.new_password}
                  onChange={(e) =>
                    setDev({ ...dev, new_password: e.target.value })
                  }
                  placeholder="leave blank to keep"
                  data-testid="dev-new-password"
                />
              </div>
            </div>

            <div className="bg-[#FFF6E8] border border-[#F4D58B] text-[#7A5A18] rounded-md p-3 text-xs">
              <strong>How this works:</strong> The developer (you, the software
              provider) sets a per-tenant outlet cap with this password. Once
              set, the restaurant admin cannot create more outlets than this
              limit, even though they have admin role. Default password is{" "}
              <code>dev-change-me</code> — rotate immediately after deployment.
            </div>

            <Button
              onClick={saveDev}
              className="bg-[#D95D39] hover:bg-[#C24F2C]"
              data-testid="save-dev-settings"
            >
              <Save size={14} className="mr-1.5" /> Apply Developer Limits
            </Button>
            {devUnlocked && (
              <div className="text-xs text-[#6A7F66]">
                ✓ Developer limits saved
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="theme" className="mt-4">
          <Card className="p-5 border-[#EBE8E2] bg-white max-w-2xl">
            <div className="font-heading font-bold text-lg text-[#2D2C2A] mb-1">
              Brand Theme
            </div>
            <div className="text-xs text-[#7A7873] mb-4">
              Pick a colour palette. Changes are applied instantly across the
              app.
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(THEMES).map(([k, t]) => (
                <button
                  key={k}
                  onClick={() => pickTheme(k)}
                  data-testid={`theme-${k}`}
                  className={`border-2 rounded-lg p-3 text-left transition hover:shadow-sm ${theme === k ? "border-[#2D2C2A]" : "border-[#EBE8E2]"}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-6 h-6 rounded-md"
                      style={{ backgroundColor: t.swatch }}
                    />
                    <div
                      className="w-3 h-3 rounded-md"
                      style={{ backgroundColor: t.vars["--theme-secondary"] }}
                    />
                  </div>
                  <div className="font-medium text-sm text-[#2D2C2A]">
                    {t.label.split(" (")[0]}
                  </div>
                  <div className="text-[10px] text-[#7A7873]">
                    {t.label.match(/\((.*)\)/)?.[1] || ""}
                  </div>
                  {theme === k && (
                    <div
                      className="text-[10px] mt-1 font-semibold"
                      style={{ color: t.swatch }}
                    >
                      ✓ Active
                    </div>
                  )}
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
